import { useEffect, useCallback } from "react";
import ChatBlock from "../components/ChatBlock";
import RefineForm from "../components/RefineForm";
import ServicesRow from "../components/ServicesRow";

interface BlockData {
  id: string;
  type: "chat" | "services" | "feedback" | "refine";
  data: any;
  timestamp: number;
}

interface SessionData {
  sessionId: string;
  startTime: number;
  lastActivity: number;
  blocks: BlockData[];
  currentQuery?: string;
  queryObject?: any;
  servicesObject?: any;
  refineFormData?: {
    questions: string[];
    answers: string[];
    isActive: boolean;
  };
}

const useRecoEngineSession = () => {
  const SESSION_KEY = "recoEngine_session";
  const SESSION_TIMEOUT = 60 * 60 * 1000; // 1 hour

  // Initialize or get existing session
  const initializeSession = useCallback(() => {
    const existingSession = sessionStorage.getItem(SESSION_KEY);
    
    if (existingSession) {
      const session: SessionData = JSON.parse(existingSession);
      const now = Date.now();
      
      // Check if session is still valid
      if (now - session.lastActivity < SESSION_TIMEOUT) {
        // Update last activity
        session.lastActivity = now;
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
        return session;
      } 
        // Session expired, clear it
        sessionStorage.removeItem(SESSION_KEY);
      
    }
    
    // Create new session
    const newSession: SessionData = {
      sessionId: `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      startTime: Date.now(),
      lastActivity: Date.now(),
      blocks: []
    };
    
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(newSession));
    return newSession;
  }, []);

  // Get current session
  const getSession = useCallback((): SessionData | null => {
    const sessionData = sessionStorage.getItem(SESSION_KEY);
    if (!sessionData) return null;
    
    const session: SessionData = JSON.parse(sessionData);
    const now = Date.now();
    
    // Check if session is still valid
    if (now - session.lastActivity < SESSION_TIMEOUT) {
      return session;
    } 
      // Session expired, clear it
      sessionStorage.removeItem(SESSION_KEY);
      return null;
    
  }, []);

  // Update session activity
  const updateActivity = useCallback(() => {
    const session = getSession();
    if (session) {
      session.lastActivity = Date.now();
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
    }
  }, [getSession]);

  // Add a block to the session
  const addBlock = useCallback((block: Omit<BlockData, "id" | "timestamp">) => {
    const session = getSession();
    if (session) {
      const newBlock: BlockData = {
        ...block,
        id: `block_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: Date.now()
      };
      
      session.blocks.push(newBlock);
      session.lastActivity = Date.now();
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
    }
  }, [getSession]);

  // Update session data
  const updateSessionData = useCallback((data: Partial<SessionData>) => {
    const session = getSession();
    if (session) {
      const updatedSession = { ...session, ...data, lastActivity: Date.now() };
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(updatedSession));
    }
  }, [getSession]);

  // Clear session
  const clearSession = useCallback(() => {
    sessionStorage.removeItem(SESSION_KEY);
  }, []);

  // Check if user is in session
  const isInSession = useCallback(() => getSession() !== null, [getSession]);

  // Get blocks for rendering
  const getBlocks = useCallback(() => {
    const session = getSession();
    return session?.blocks || [];
  }, [getSession]);

  // Get refine form data
  const getRefineFormData = useCallback(() => {
    const session = getSession();
    return session?.refineFormData;
  }, [getSession]);

  // Set refine form data
  const setRefineFormData = useCallback((refineFormData: any) => {
    const session = getSession();
    if (session) {
      session.refineFormData = refineFormData;
      session.lastActivity = Date.now();
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
    }
  }, [getSession]);

  const restoreRenderedBlocks = useCallback(() => {
    const session = getSession();
    if (!session) return [];
    
    return session.blocks.map(block => {
      switch (block.type) {
        case "chat":
          return {
            type: "chat",
            component: (
              <ChatBlock 
                key={block.id} 
                text={block.data.text} 
                engineResponse={block.data.engineResponse}
                loading={block.data.loading}
              />
            )
          };
        case "services":
          return {
            type: "services",
            component: (
              <ServicesRow 
                key={block.id} 
                services={block.data.services} 
              />
            )
          };
        case "feedback":
          return {
            type: "feedback",
            component: null,
            shouldShowFeedbackRow: true
          };
        case "refine":
          return {
            type: "refine",
            component: (
              <RefineForm
                key={block.id} 
                mode="display"
                questions={block.data.questions} 
                answers={block.data.answers} 
              />
            )
          };
        default:
          return null;
      }
    }).filter(Boolean);
  }, [getSession]);

  const initializePage = useCallback(() => {
    const session = initializeSession();
    
    if (session.blocks.length > 0) {
      // Restore from existing session
      return {
        hasExistingSession: true,
        session,
      };
    } 
      // Fresh session
      return {
        hasExistingSession: false,
        session,
        showBanner: true,
        showChatBar: true,
        readyForQuery: true
      };
    
  }, [initializeSession]);

  // Initialize blocks on page
  const createInitialBlocks = useCallback((queryObject, query, t, addBlockCallback) => {
    const session = getSession();
    if (!session || session.blocks.length > 0) {
      return []; // No blocks to create
    }
    
    const initialBlocks = [];
    
    // Always add starting message on first visit
    const startingBlock = {
      type: "chat" as const,
      data: {
        text: t("RecoSearch.starting_message"),
        engineResponse: true
      }
    };
    addBlockCallback(startingBlock);
    initialBlocks.push(
      <ChatBlock 
        key="starting-message" 
        text={t("RecoSearch.starting_message")} 
        engineResponse 
      />
    );
    
    // Add user query if available and not already added by navigation
    if (queryObject.query && !query) {
      const userQueryBlock = {
        type: "chat" as const,
        data: {
          text: queryObject.query,
          engineResponse: false
        }
      };
      addBlockCallback(userQueryBlock);
      initialBlocks.push(
        <ChatBlock 
          key="user-query" 
          text={queryObject.query} 
          engineResponse={false}
        />
      );
    }
    
    return initialBlocks;
  }, [getSession, addBlock]);

  // Update activity on mount
  useEffect(() => {
    updateActivity();
  }, [updateActivity]);

  return {
    initializeSession,
    getSession,
    updateActivity,
    addBlock,
    updateSessionData,
    clearSession,
    isInSession,
    getBlocks,
    getRefineFormData,
    setRefineFormData,
    initializePage,
    createInitialBlocks,
    restoreRenderedBlocks
  };
};

export default useRecoEngineSession;
